import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { FORMS_SSO_DASHBOARD_PATH } from '@/lib/formsUrls';
import { openFormsSsoTab } from '@/lib/formsStaffAccess';
import { fetchFormsSsoUrl } from '@/hooks/useFormsStaffAccess';

export function FormsSsoButton({
  clinicId,
  hasGrant,
  redirectPath = FORMS_SSO_DASHBOARD_PATH,
  label,
  variant = 'primary',
  className,
}: {
  clinicId: string;
  hasGrant: boolean;
  redirectPath?: string;
  label: string;
  variant?: 'primary' | 'secondary';
  className?: string;
}) {
  const { t } = useTranslation();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!hasGrant) {
    return (
      <div className={cn('max-w-xs', className)}>
        <Button type="button" variant={variant} disabled className="w-full sm:w-auto">
          <ExternalLink />
          {label}
        </Button>
        <p className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>
          {t('questionnaires.access.ask_admin')}
        </p>
      </div>
    );
  }

  const handleClick = async () => {
    setError(null);
    setPending(true);
    try {
      await openFormsSsoTab(() => fetchFormsSsoUrl(clinicId, redirectPath));
    } catch (err) {
      const message = err instanceof Error ? err.message : t('questionnaires.access.sso_error');
      setError(
        message === 'forms_owner'
          ? t('questionnaires.access.owner_cannot_remove')
          : message === 'account-in-other-tenant'
            ? t('questionnaires.access.account_in_other_tenant')
            : message,
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <div className={className}>
      <Button type="button" variant={variant} onClick={() => void handleClick()} disabled={pending}>
        {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink />}
        {label}
      </Button>
      {error ? (
        <p className={cn(TYPE.small, 'mt-s1 text-ds-red-ink')}>{error}</p>
      ) : null}
    </div>
  );
}
